// Hook genérico para tablas estandarizadas
// Basado en useInventoryQueryTable pero más flexible
import { useCallback, useMemo } from 'react';

// Tipo genérico para items de tabla
export interface TableItem {
  id: string | number;
  [key: string]: any;
}

// Función para determinar si un item es seleccionable
export type IsSelectableFn<T extends TableItem> = (item: T) => boolean;

// Props del hook genérico
export interface UseTableProps<T extends TableItem> {
  items: T[];
  selectedItems: (string | number)[];
  onSelectItems: (items: (string | number)[]) => void;
  isSelectable?: IsSelectableFn<T>;
  getItemId?: (item: T) => string | number;
}

// Retorno del hook
export interface UseTableReturn<T extends TableItem = TableItem> {
  selectableItems: T[];
  isAllSelected: boolean;
  handleSelectItem: (id: string | number) => void;
  handleSelectAll: () => void;
  getCheckboxStyles: (isEnabled: boolean) => { opacity: number; cursor: string };
}

/**
 * Hook genérico para manejo de selección en tablas
 */
export const useTable = <T extends TableItem>({
  items,
  selectedItems,
  onSelectItems,
  isSelectable,
  getItemId = (item) => item.id,
}: UseTableProps<T>): UseTableReturn<T> => {
  // Filtrar items seleccionables
  const selectableItems = useMemo(() => {
    if (!isSelectable) {
      // Si no hay función de selección, todos son seleccionables
      return items;
    }
    return items.filter(isSelectable);
  }, [items, isSelectable]);

  // Verificar si todos los items seleccionables están seleccionados
  const isAllSelected = useMemo(() => {
    if (selectableItems.length === 0) return false;
    return selectableItems.every((item) => {
      const id = getItemId(item);
      return selectedItems.includes(id);
    });
  }, [selectableItems, selectedItems, getItemId]);

  // Manejo de selección individual
  const handleSelectItem = useCallback(
    (id: string | number) => {
      const newSelectedItems = selectedItems.includes(id)
        ? selectedItems.filter((itemId) => itemId !== id)
        : [...selectedItems, id];

      onSelectItems(newSelectedItems);
    },
    [selectedItems, onSelectItems],
  );

  // Manejo de selección de todos
  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // Deseleccionar todos los items de la página actual
      const currentPageIds = items.map(getItemId);
      const newSelectedItems = selectedItems.filter((id) => !currentPageIds.includes(id));
      onSelectItems(newSelectedItems);
    } else {
      // Seleccionar solo los items seleccionables de la página actual
      const currentPageSelectableIds = selectableItems.map(getItemId);
      const newSelectedItems = [...selectedItems];

      currentPageSelectableIds.forEach((id) => {
        if (!newSelectedItems.includes(id)) {
          newSelectedItems.push(id);
        }
      });

      onSelectItems(newSelectedItems);
    }
  }, [isAllSelected, selectedItems, items, selectableItems, onSelectItems, getItemId]);

  // Estilos comunes para checkboxes
  const getCheckboxStyles = useCallback((isEnabled: boolean) => {
    if (isEnabled) {
      return {
        opacity: 1,
        cursor: 'pointer' as const,
      };
    } else {
      return {
        opacity: 0.3,
        cursor: 'not-allowed' as const,
      };
    }
  }, []);

  return {
    selectableItems,
    isAllSelected,
    handleSelectItem,
    handleSelectAll,
    getCheckboxStyles,
  };
};

// Helper para obtener propiedades de chips según estado
export type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

export interface ChipProps {
  color: ChipColor;
  className?: string;
}

export type GetChipPropsFn = (status: string, context?: any) => ChipProps;

/**
 * Función helper genérica para obtener propiedades de chips
 * Puede ser sobrescrita por implementaciones específicas
 */
export const getChipPropsDefault: GetChipPropsFn = (status: string) => {
  const statusLower = status.toLowerCase();

  if (
    statusLower.includes('disponible') ||
    statusLower.includes('activo') ||
    statusLower.includes('asignado')
  ) {
    return { color: 'success' as const, className: 'bg-green-100 text-green-800' };
  }
  if (statusLower.includes('vendido') || statusLower.includes('completado')) {
    return { color: 'info' as const, className: 'bg-blue-100 text-blue-800' };
  }
  if (statusLower.includes('reservado') || statusLower.includes('pendiente')) {
    return { color: 'warning' as const, className: 'bg-yellow-100 text-yellow-800' };
  }
  if (statusLower.includes('error') || statusLower.includes('cancelado')) {
    return { color: 'error' as const, className: 'bg-red-100 text-red-800' };
  }

  return { color: 'default' as const, className: 'bg-gray-100 text-gray-800' };
};
